<script setup lang="ts">
import {formatting, LegacyInvoiceRow, LegacyOrder, legacyOrderKey, useContext, usePhrases} from '@hantera/portal-app';
import {PanelContent} from '@hantera/design-system';
import Decimal from 'decimal.js';
import {computed, inject, ref} from 'vue';

const props = defineProps<{
  row: LegacyInvoiceRow
}>();

const order = inject(legacyOrderKey, ref<LegacyOrder>(<any>{}));
const context = useContext();
const phrase = usePhrases('invoice');
const { currency, percent } = formatting;

const orderRow = computed(() => {
  if (props.row.referenceType !== 'Row') {
    return;
  }

  return order.value.deliveries.flatMap(r => r.rows).find(r => r.rowId === props.row.referenceId);
});

const delivery = computed(() => {
  if (props.row.referenceType !== 'Delivery') {
    return;
  }

  return order.value.deliveries.find(r => r.deliveryId === props.row.referenceId);
});

const imageUrl = computed(() => {
  if (!orderRow.value) {
    return;
  }

  return context.serverUrl() + '/legacy' + orderRow.value.item.image;
});

const description1 = computed(() => {
  if (delivery.value) {
    return delivery.value.deliveryNumber;
  }

  if (orderRow.value) {
    return orderRow.value.item.articleNumber;
  }
});

const description2 = computed(() => {
  if (delivery.value) {
    return phrase('Shipping');
  }

  if (orderRow.value) {
    return orderRow.value.item.name;
  }
});

const net = computed(() => {
  if (props.row.invoiceRowTotal.eq(0)) {
    return new Decimal(0);
  }
  return props.row.invoiceRowTotal.sub(props.row.invoiceRowVat);
});

const vat = computed(() => {
  const net = props.row.invoiceRowTotal.sub(props.row.invoiceRowVat)
  if (net.eq(0)) {
    return new Decimal(0);
  }
  
  return props.row.invoiceRowVat.div(net);
});

</script>

<template>
  <PanelContent class="invoice-row">
    <div class="invoice-row__image">
      <img
        :src="imageUrl"
        v-if="imageUrl"
      />
    </div>
    <div class="invoice-row__description1" :title="description1">
      {{ description1 }}
    </div>
    <div class="invoice-row__description2" :title="description2">
      {{ description2 }}
    </div>
    <div class="invoice-row__value-label">
      {{ phrase('Net') }}
    </div>
    <div class="invoice-row__value">
      {{ currency(net, order.currencyCode) }}
    </div>
    <div class="invoice-row__value-label">
      {{ phrase('VAT') }}
    </div>
    <div class="invoice-row__value">
      {{ percent(vat) }}
    </div>
    <div class="invoice-row__value-label">
      {{ phrase('Sum') }}
    </div>
    <div class="invoice-row__value">
      {{ currency(row.invoiceRowTotal, order.currencyCode) }}
    </div>
  </PanelContent>
</template>

<style>
.invoice-row>.panel-content__body {
  display: grid;
  grid-template-columns: 40px auto repeat(3, minmax(70px, max-content));
  grid-template-rows: 20px 20px;
  grid-auto-rows: 16px;
  column-gap: 10px;
  row-gap: 2px;
  white-space: nowrap;
  padding: 2px 3px;
  margin-bottom: 5px;

  line-height: 16px;
}

.invoice-row__image {
  position: relative;
  grid-column: 1;
  grid-row: 1 / 3;
}

.invoice-row__image img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.invoice-row__description1 {
  font-weight: bold;
}

.invoice-row__description1,
.invoice-row__value-label {
  grid-row: 1 / 2;
}

.invoice-row__description2,
.invoice-row__value {
  grid-row: 2 / 3;
}

.invoice-row__value-label,
.invoice-row__value {
  text-align: right;
}

@container (max-width: 550px) {
  .invoice-row>.panel-content__body {
    grid-template-columns: 40px auto repeat(3, min-content);
    column-gap: 5px;
  }

  .invoice-row__description1,
  .invoice-row__description2 {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
}
</style>